/**
 * 导出场景水印：在系统水印（`@home/utils/watermark`）之上，
 * 提供 HTML 注入 / Canvas 叠加能力。
 *
 * @module @home/utils/htmlExport/watermark
 */

import {
  genWatermarkDataUrl,
  type IWatermarkConfig,
} from '@home/utils/watermark';

export interface IBuildWatermarkDataUrlOptions {
  /** 主文案，如 `userid@DataBrain` */
  text: string;
  /** 第二行（登录 code），可选 */
  loginCode?: string;
  /** 覆盖默认颜色 */
  color?: string;
}

/**
 * 生成可平铺的水印 Data URL（内部调用系统 {@link genWatermarkDataUrl}）
 *
 * @param textOrOptions - 主文案字符串，或完整配置对象
 * @param loginCode - 当第一个参数为字符串时的第二行文案
 */
export function buildWatermarkDataUrl(
  textOrOptions: string | IBuildWatermarkDataUrlOptions,
  loginCode = '',
): string {
  const options: IBuildWatermarkDataUrlOptions =
    typeof textOrOptions === 'string'
      ? { text: textOrOptions, loginCode }
      : textOrOptions;

  const config: IWatermarkConfig = {
    text: options.text || '',
    loginCode: options.loginCode || '',
  };
  if (options.color) {
    config.color = options.color;
  }
  return genWatermarkDataUrl(config);
}

/**
 * 在已有 canvas 上叠加平铺水印（用于 PDF 截图后叠加）
 * 水印图加载失败时跳过，不影响主流程
 */
export async function applyWatermarkToCanvas(
  srcCanvas: HTMLCanvasElement,
  watermarkDataUrl: string,
): Promise<HTMLCanvasElement> {
  if (!watermarkDataUrl) {
    return srcCanvas;
  }

  const out = document.createElement('canvas');
  out.width = srcCanvas.width;
  out.height = srcCanvas.height;
  const ctx = out.getContext('2d');
  if (!ctx) {
    return srcCanvas;
  }

  ctx.drawImage(srcCanvas, 0, 0);

  await new Promise<void>((resolve) => {
    const img = new Image();
    img.onload = () => {
      const pattern = ctx.createPattern(img, 'repeat');
      if (pattern) {
        ctx.fillStyle = pattern;
        ctx.fillRect(0, 0, out.width, out.height);
      }
      resolve();
    };
    img.onerror = () => resolve();
    img.src = watermarkDataUrl;
  });

  return out;
}

/**
 * 在 HTML 字符串的 `</body>` 前注入全屏固定水印层
 * 若无 `</body>`，则追加到字符串末尾
 *
 * 注：使用大小写不敏感匹配，兼容 `</BODY>` 等非标准写法
 */
export function injectWatermarkIntoHtml(
  html: string,
  watermarkDataUrl: string,
): string {
  if (!watermarkDataUrl) {
    return html;
  }

  const watermarkDiv = `
<div style="
  position:fixed;
  pointer-events:none;
  top:0;left:0;
  width:100%;height:100%;
  z-index:9999999999;
  background:url('${watermarkDataUrl}') repeat;
"></div>`;

  // 大小写不敏感匹配 </body>，兼容非标准 HTML
  const bodyCloseTag = html.match(/<\/body>/i)?.[0];
  return bodyCloseTag
    ? html.replace(bodyCloseTag, `${watermarkDiv}${bodyCloseTag}`)
    : `${html}${watermarkDiv}`;
}

/**
 * 根据 watermark 配置解析最终可用的 Data URL
 * 优先 `dataUrl`，否则用系统水印生成；都没有则返回空串
 */
export function resolveWatermarkDataUrl(options?: {
  dataUrl?: string;
  text?: string;
  loginCode?: string;
  color?: string;
}): string {
  if (!options) {
    return '';
  }
  if (options.dataUrl) {
    return options.dataUrl;
  }
  if (options.text) {
    return buildWatermarkDataUrl({
      text: options.text,
      loginCode: options.loginCode,
      color: options.color,
    });
  }
  return '';
}
